import { CloseOutlined } from '@mui/icons-material';
import { Alert, Box, IconButton } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import type { ApiKey } from '@/hooks/useApiKeys';

import RevokeApiKeyDialog from './RevokeApiKeyDialog';

const columnHelper = createColumnHelper<ApiKey>();

export interface ApiKeysTableProps {
  apiKeys: ApiKey[];
  isLoading: boolean;
  isRefetching: boolean;
  error: Error | null | undefined;
  organizationId: number;
}

const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateString || '-';
  }
};

function ApiKeysTable({ apiKeys, isLoading, isRefetching, error, organizationId }: ApiKeysTableProps) {
  const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('system', {
        header: 'External System',
        size: 240,
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('publicKey', {
        header: 'Public Key',
        size: 360,
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('createdUtc', {
        header: 'Date Created',
        size: 160,
        cell: (info) => formatDateTime(info.getValue()),
      }),
      columnHelper.display({
        id: 'action',
        cell: (info) => {
          const row = info.row.original;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <IconButton
                aria-label='remove-api-key'
                data-testid={`remove-api-key-${row.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.id) {
                    setSelectedKeyId(row.id);
                  }
                }}
              >
                <CloseOutlined sx={{ fontSize: '14px' }} />
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    [],
  );

  const tableWithData = useBaseTable<ApiKey>({
    tableData: apiKeys,
    columns,
    isRowSelectionEnabled: false,
    isColumnVisibilityEnabled: false,
    isColumnOrderEnabled: false,
    hasPagination: false,
  });

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-api-keys'
      >
        Error: {error.message}
      </Alert>
    );
  }

  return (
    <>
      <BaseTable<ApiKey>
        {...tableWithData}
        testId='api-keys-table'
        ariaLabel='API keys list'
        noDataMessage='No API keys found.'
        loadingData={{
          loadingMessage: 'Loading API Keys…',
          isLoading: isLoading,
        }}
      />
      {selectedKeyId !== null && (
        <RevokeApiKeyDialog
          open={selectedKeyId !== null}
          onClose={() => setSelectedKeyId(null)}
          organizationId={organizationId}
          keyId={selectedKeyId}
          isRefetching={isRefetching}
        />
      )}
    </>
  );
}

export default ApiKeysTable;
